import MarkerPinIcon from "@redotech/redo-icons/marker-pin-01.svg";
import SearchIcon from "@redotech/redo-icons/search-md.svg";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoRadioButton } from "@redotech/redo-web/arbiter-components/radio/redo-radio-button";
import { Divider } from "@redotech/redo-web/divider";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo, useState } from "react";
import * as styles from "./move-to-fulfillment-center-modal.module.css";

export interface ReassignmentLocationItem {
  lineItemId: string;
  name: string;
  sku: string;
  imageUrl?: string | null;
  needed: number;
  onHand: number;
}

export interface ReassignmentLocationOption {
  externalLocationId: string;
  name: string;
  region?: string | null;
  mappingAliases: string[];
  items: ReassignmentLocationItem[];
}

export type Pickability = "all" | "partial" | "none";

export const PICK_META: Record<
  Pickability,
  { color: "success" | "warning" | "error"; label: string }
> = {
  all: { color: "success", label: "All pickable" },
  partial: { color: "warning", label: "Partially pickable" },
  none: { color: "error", label: "Not pickable" },
};

export function coverageOf(items: readonly ReassignmentLocationItem[]): {
  pickability: Pickability;
  coveredCount: number;
} {
  const tracked = items.filter((i) => i.onHand >= 0);
  const coveredCount = tracked.filter((i) => i.onHand >= i.needed).length;
  if (tracked.length > 0 && coveredCount === tracked.length) {
    return { pickability: "all", coveredCount };
  }
  return { pickability: coveredCount > 0 ? "partial" : "none", coveredCount };
}

interface MoveToFulfillmentCenterModalProps {
  isOpen: boolean;
  serviceName: string;
  orderName: string;
  totalUnits: number;
  locations: readonly ReassignmentLocationOption[];
  onClose: () => void;
  onConfirm: (externalLocationId: string) => void;
}

export const MoveToFulfillmentCenterModal = memo(
  function MoveToFulfillmentCenterModal({
    isOpen,
    serviceName,
    orderName,
    totalUnits,
    locations,
    onClose,
    onConfirm,
  }: MoveToFulfillmentCenterModalProps) {
    const [query, setQuery] = useState("");
    const [selectedId, setSelectedId] = useState<string | null>(null);

    const visible = useMemo(() => {
      const q = query.trim().toLowerCase();
      const filtered = locations.filter(
        (l) => !q || l.name.toLowerCase().includes(q),
      );
      const rank = (l: ReassignmentLocationOption) => {
        const { pickability } = coverageOf(l.items);
        return pickability === "all" ? 0 : pickability === "partial" ? 1 : 2;
      };
      return [...filtered].sort(
        (a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name),
      );
    }, [locations, query]);

    const selected = useMemo(
      () => locations.find((l) => l.externalLocationId === selectedId) ?? null,
      [locations, selectedId],
    );

    const selectedCoverage = selected ? coverageOf(selected.items) : null;

    const subtitle = `${orderName} · ${totalUnits} units`;

    return (
      <RedoModal
        hideFooter
        isOpen={isOpen}
        modalSize="lg"
        noPadding
        onModalCloseRequested={onClose}
        subtitle={subtitle}
        title={`Move to ${serviceName}`}
      >
        <Flex className={styles.content}>
          <Flex className={styles.leftPanel} dir="column">
            <Flex align="center" justify="space-between" px="xl" py="lg">
              <Text fontSize="sm" fontWeight="semibold">
                Destination location
              </Text>
              <Text fontSize="xs" textColor="secondary">
                {visible.length} of {locations.length}
              </Text>
            </Flex>
            <Divider />
            <Flex px="xl" py="md">
              <RedoTextInput
                IconLeading={SearchIcon}
                placeholder="Search locations"
                setValue={setQuery}
                value={query}
              />
            </Flex>
            <Divider />
            <Flex className={styles.scrollable} dir="column">
              {visible.length === 0 && (
                <Flex justify="center" p="xl">
                  <Text fontSize="sm" textColor="secondary">
                    {query
                      ? `No locations match "${query}".`
                      : "No eligible locations."}
                  </Text>
                </Flex>
              )}
              {visible.map((loc) => {
                const active = loc.externalLocationId === selectedId;
                const { pickability } = coverageOf(loc.items);
                const meta = PICK_META[pickability];
                return (
                  <Flex
                    align="center"
                    className={
                      active ? styles.locationRowSelected : styles.locationRow
                    }
                    gap="md"
                    key={loc.externalLocationId}
                    onClick={() => setSelectedId(loc.externalLocationId)}
                    px="xl"
                    py="md"
                  >
                    <RedoRadioButton
                      onSelect={() => setSelectedId(loc.externalLocationId)}
                      selected={active}
                    />
                    <Flex className={styles.flexFill} dir="column" gap="none">
                      <Text fontSize="sm" fontWeight="semibold">
                        {loc.name}
                      </Text>
                      <Text fontSize="xs" textColor="secondary">
                        {[
                          loc.region,
                          loc.mappingAliases.length > 0
                            ? loc.mappingAliases.join(", ")
                            : null,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </Text>
                    </Flex>
                    <RedoBadge color={meta.color} size="sm" text={meta.label} />
                  </Flex>
                );
              })}
            </Flex>
          </Flex>

          <Flex className={styles.flexFill} dir="column">
            {!selected || !selectedCoverage ? (
              <Flex
                align="center"
                className={styles.emptyState}
                dir="column"
                gap="md"
                justify="center"
                p="2xl"
              >
                <MarkerPinIcon />
                <Text fontSize="sm" textAlign="center" textColor="secondary">
                  Choose a destination to preview pickable inventory for this
                  order.
                </Text>
              </Flex>
            ) : (
              <>
                <Flex dir="column" gap="xs" px="xl" py="lg">
                  <Flex align="center" gap="sm">
                    <MarkerPinIcon />
                    <Text fontSize="md" fontWeight="semibold">
                      {selected.name}
                    </Text>
                  </Flex>
                  {selected.region && (
                    <Text fontSize="xs" textColor="secondary">
                      {selected.region}
                    </Text>
                  )}
                  <Flex align="center" gap="md" pt="xs">
                    <RedoBadge
                      color={PICK_META[selectedCoverage.pickability].color}
                      size="sm"
                      text={PICK_META[selectedCoverage.pickability].label}
                    />
                    <Text fontSize="xs" textColor="secondary">
                      {selectedCoverage.coveredCount} of {selected.items.length}{" "}
                      items in stock
                    </Text>
                  </Flex>
                </Flex>
                <Divider />
                <Flex className={styles.scrollable} dir="column">
                  {selected.items.map((item, i) => {
                    const unknown = item.onHand < 0;
                    const enough = !unknown && item.onHand >= item.needed;
                    return (
                      <div key={item.lineItemId}>
                        {i > 0 && <Divider />}
                        <Flex
                          align="center"
                          gap="md"
                          justify="space-between"
                          px="xl"
                          py="lg"
                        >
                          <Flex
                            align="center"
                            className={styles.flexFill}
                            gap="md"
                          >
                            {item.imageUrl ? (
                              <img
                                alt={item.name}
                                className={styles.itemThumbnail}
                                src={item.imageUrl}
                              />
                            ) : (
                              <div className={styles.itemThumbnail} />
                            )}
                            <Flex dir="column" gap="none">
                              <Text fontSize="sm" fontWeight="medium">
                                {item.name}
                              </Text>
                              <Text fontSize="xs" textColor="secondary">
                                SKU {item.sku} · need {item.needed}
                              </Text>
                            </Flex>
                          </Flex>
                          <Text
                            fontSize="sm"
                            fontWeight="medium"
                            textColor={
                              unknown
                                ? "secondary"
                                : enough
                                  ? "primary"
                                  : "secondary"
                            }
                          >
                            {unknown
                              ? "Unknown"
                              : item.onHand === 0
                                ? "Out of stock"
                                : `${item.onHand} in stock`}
                          </Text>
                        </Flex>
                      </div>
                    );
                  })}
                </Flex>
              </>
            )}
          </Flex>
        </Flex>

        <Flex
          align="center"
          className={styles.footer}
          justify="space-between"
          px="xl"
          py="lg"
        >
          <Text fontSize="xs" textColor="secondary">
            {selected
              ? `Moving ${orderName} via ${serviceName}`
              : "Pick a destination to continue"}
          </Text>
          <Flex gap="md">
            <RedoButton
              hierarchy="secondary"
              onClick={onClose}
              size="md"
              text="Cancel"
            />
            <RedoButton
              disabled={!selected}
              hierarchy="primary"
              onClick={() => selected && onConfirm(selected.externalLocationId)}
              size="md"
              text={selected ? `Move to ${selected.name}` : "Move order"}
            />
          </Flex>
        </Flex>
      </RedoModal>
    );
  },
);
